// Times on every screen (guide §6.8k, 4.3d.4): in the BUSINESS's time zone and the viewer's language.
//
// THE RULE the API keeps (server/ai/dbClock.js): every time it sends is an instant ('…Z'), and a calendar date (a due date,
// a close date) is a bare day ('YYYY-MM-DD'). The screens show an instant in the business's own zone — the one set in
// Settings → Working hours (Profile.Timezone) — whoever is looking and wherever they are: a manager abroad and an agent at
// the desk read the same 14:00 the customer was promised. A time a person types is read in that zone too.
//
// setBusinessTimeZone is called when the profile arrives (ProfileSlice). From then on:
//   - these helpers format and count days in that zone;
//   - every Date#toLocaleString / toLocaleDateString / toLocaleTimeString in the app that names no zone of its own uses it
//     (one default for screens that format a time directly, so none is left showing the viewer's or the server's clock);
//   - bizDayjs() is dayjs in that zone, for the screens built on dayjs.
// Before the profile arrives, and with no zone set, the browser's own zone is used. Pure apart from that one switch:
// scripts/check-times.cjs calls these.
import i18next from "i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";

dayjs.extend(utc);
dayjs.extend(timezone);

export const langOf = () => i18next.language || undefined;

let BUSINESS_TZ = null;
const validZone = (tz) => { try { new Intl.DateTimeFormat("en", { timeZone: tz }); return true; } catch { return false; } };
export const businessTimeZone = () => BUSINESS_TZ || undefined;

// The default every unzoned Date#toLocale*String takes — installed once. No prototype (4.5, 2026-10-26): a plain {} answered
// ORIGINAL.toLocaleString with Object.prototype.toLocaleString before this was installed, and dateTimeText printed Date#toString.
const ORIGINAL = Object.create(null);
function installDefault() {
  if (ORIGINAL.done || typeof Date === "undefined") return;
  for (const name of ["toLocaleString", "toLocaleDateString", "toLocaleTimeString"]) {
    const fn = Date.prototype[name];
    ORIGINAL[name] = fn;
    Date.prototype[name] = function (locales, options) {
      if (!BUSINESS_TZ || (options && options.timeZone)) return fn.call(this, locales, options);
      return fn.call(this, locales, { ...(options || {}), timeZone: BUSINESS_TZ });
    };
  }
  ORIGINAL.done = true;
}
export function setBusinessTimeZone(tz) {
  const z = String(tz ?? "").trim();
  BUSINESS_TZ = z && validZone(z) ? z : null;
  installDefault();
  try { if (BUSINESS_TZ) dayjs.tz.setDefault(BUSINESS_TZ); else dayjs.tz.setDefault(); } catch { /* an old dayjs */ }
  return BUSINESS_TZ;
}

const valid = (v) => { if (v === null || v === undefined || v === "") return null; const d = new Date(v); return Number.isNaN(+d) ? null : d; };
const zoned = (opts) => (BUSINESS_TZ && !opts?.timeZone ? { ...(opts || {}), timeZone: BUSINESS_TZ } : opts);
const fmt = (fn, d, lang, opts) => { const f = (ORIGINAL[fn] || Date.prototype[fn]); try { return f.call(d, lang, zoned(opts)); } catch { return f.call(d, undefined, zoned(opts)); } };
const rtf = (lang) => { try { return new Intl.RelativeTimeFormat(lang, { numeric: "auto" }); } catch { return new Intl.RelativeTimeFormat(undefined, { numeric: "auto" }); } };

// The wall-clock parts of an instant in a zone (the business's by default).
export function partsIn(value, tz = BUSINESS_TZ) {
  const d = valid(value);
  if (!d) return null;
  if (!tz) return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate(), h: d.getHours(), mi: d.getMinutes() };
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
    .formatToParts(d).filter((x) => x.type !== "literal").map((x) => [x.type, Number(x.value)]));
  return { y: p.year, m: p.month, d: p.day, h: p.hour === 24 ? 0 : p.hour, mi: p.minute };
}
// The instant of a wall-clock time in a zone (the business's by default) — what a person meant by "14:00 on the 26th".
export function wallToInstant(y, m, d, h = 0, mi = 0, tz = BUSINESS_TZ) {
  if (!tz) return new Date(y, m - 1, d, h, mi, 0, 0);
  const guess = Date.UTC(y, m - 1, d, h, mi);
  const offsetAt = (t) => { const p = partsIn(new Date(t), tz); return Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi) - t; };
  let t = guess - offsetAt(guess);
  const second = guess - offsetAt(t);
  if (second !== t) t = second;
  return new Date(t);
}
const dayNumber = (p) => Date.UTC(p.y, p.m - 1, p.d) / 86400000;

// "5 minutes ago", "yesterday", then the date.
export function relativeTime(value, lang = langOf(), now = Date.now()) {
  const d = valid(value);
  if (!d) return "";
  const min = Math.floor((now - d.getTime()) / 60000);
  const r = rtf(lang);
  if (min < 1) return r.format(0, "second");
  if (min < 60) return r.format(-min, "minute");
  const hr = Math.floor(min / 60);
  if (hr < 24) return r.format(-hr, "hour");
  const day = Math.floor(hr / 24);
  if (day < 30) return r.format(-day, "day");
  return dateText(d, lang);
}

export const dateText = (value, lang = langOf(), opts = { day: "2-digit", month: "short" }) => { const d = valid(value); return d ? fmt("toLocaleDateString", d, lang, opts) : ""; };
export const timeText = (value, lang = langOf()) => { const d = valid(value); return d ? fmt("toLocaleTimeString", d, lang, { hour: "2-digit", minute: "2-digit" }) : ""; };
export const dateTimeText = (value, lang = langOf(), opts = { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) => {
  const d = valid(value); return d ? fmt("toLocaleString", d, lang, opts) : "";
};

// "Today, 14:05" / "Yesterday, 09:10" / "26 Oct, 14:05" — days counted in the business's zone.
export function dayAndTime(value, lang = langOf(), now = new Date()) {
  const d = valid(value);
  if (!d) return "";
  const days = dayNumber(partsIn(now)) - dayNumber(partsIn(d));
  const word = days === 0 || days === 1 ? rtf(lang).format(-days, "day") : dateText(d, lang);
  return `${word.charAt(0).toLocaleUpperCase(lang) + word.slice(1)}, ${timeText(d, lang)}`;
}

// A calendar day: 'YYYY-MM-DD' (or an instant, read as the business's day) — a bare day never moves through a zone.
const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
export function dayText(value, lang = langOf(), opts = { day: "2-digit", month: "short" }) {
  const m = DAY.exec(String(value ?? ""));
  if (m) { const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])); const f = ORIGINAL.toLocaleDateString || Date.prototype.toLocaleDateString; try { return f.call(d, lang, { ...opts, timeZone: "UTC" }); } catch { return f.call(d, undefined, { ...opts, timeZone: "UTC" }); } }
  return dateText(value, lang, opts);
}
// Whole days from the business's today to that day: 0 today, 1 tomorrow, -2 two days overdue.
export function daysUntil(value, now = new Date()) {
  const m = DAY.exec(String(value ?? ""));
  const target = m ? { y: +m[1], m: +m[2], d: +m[3] } : partsIn(value);
  if (!target) return null;
  return dayNumber(target) - dayNumber(partsIn(now));
}

// The business's day of an instant, for a date input ('YYYY-MM-DD'); a bare day stays itself.
export function localDayOf(value) {
  if (DAY.test(String(value ?? ""))) return String(value);
  const p = partsIn(value);
  return p ? `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}` : "";
}
// The business's today, 'YYYY-MM-DD' (date-range defaults), and n days before it.
export const todayDay = (now = new Date()) => localDayOf(now);
export function daysAgoDay(n, now = new Date()) {
  const p = partsIn(now);
  const t = new Date(Date.UTC(p.y, p.m - 1, p.d - n));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, "0")}-${String(t.getUTCDate()).padStart(2, "0")}`;
}

// What a person typed, as the instant they meant, read in the business's zone: a date-and-time box gives that wall time; a
// date box means "by the end of that day". Empty is null.
export function typedInstant(value) {
  const s = String(value ?? "").trim();
  if (!s) return null;
  const dm = DAY.exec(s);
  if (dm) return wallToInstant(+dm[1], +dm[2], +dm[3], 23, 59).toISOString();
  const tm = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s);
  if (tm && !/(Z|[+-]\d{2}:?\d{2})$/i.test(s)) return wallToInstant(+tm[1], +tm[2], +tm[3], +tm[4], +tm[5]).toISOString();
  const d = new Date(s);
  return Number.isNaN(+d) ? null : d.toISOString();
}
// An instant as a date-and-time box's value ('YYYY-MM-DDTHH:mm'), in the business's zone.
export function wallInputOf(value) {
  const p = partsIn(value);
  if (!p) return "";
  const two = (n) => String(n).padStart(2, "0");
  return `${p.y}-${two(p.m)}-${two(p.d)}T${two(p.h)}:${two(p.mi)}`;
}

// dayjs in the business's zone, for the screens built on dayjs: bizDayjs(instant).format(…), bizDayjs() for "now".
export function bizDayjs(value) {
  const base = value === undefined ? dayjs() : dayjs(value);
  return BUSINESS_TZ ? base.tz(BUSINESS_TZ) : base;
}
